import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import InformationIcon from 'components/atoms/icons/informationIcon';
import PatientsReportIcon from 'components/atoms/icons/patientsReportIcon';
import ButtonLoadingIcon from 'components/atoms/icons/buttonLoadingIcon';
import BaseTabs from 'components/atoms/base-tabs';
import { ModuleType } from 'infrastructure/enums';
import { useExportPatientReport } from 'infrastructure/hooks/provider-dashboard/use-export-patient-report';
import common from 'constants/common';

import s from './styles.module.scss';
import {
  emptyData,
  getPatientReportTooltipKey,
  patientReportColumnLabelMap,
  patientReportColumnExportMap,
} from './utils';

import type { IBaseTab } from 'components/atoms/base-tabs';
import type {
  IGetPatientReportsResponse,
  TCountRow,
  TTwoWaySyncRow,
} from './types';
import type { PatientReportExportCol } from './utils';

type PatientReportKey = keyof typeof patientReportColumnLabelMap;
type PatientReportEntry = [PatientReportKey, TCountRow | TTwoWaySyncRow];

export interface IPatientReportExportFilters {
  startDate: string;
  endDate: string;
  clinicGuid?: string[];
  parentClinicGuid?: string[];
  providerGuid?: string;
}

interface IPatientsReportCardProps {
  data: IGetPatientReportsResponse | undefined;
  module: ModuleType;
  isLoading?: boolean;
  setModule: React.Dispatch<React.SetStateAction<ModuleType>>;
  exportFilters: IPatientReportExportFilters;
}

export const PatientsReportCard: React.FC<IPatientsReportCardProps> = ({
  data,
  isLoading = false,
  module,
  setModule,
  exportFilters,
}) => {
  const { t } = useTranslation();
  const isRpm = module === ModuleType.RPM;

  const { exportColumn, exportingCol } = useExportPatientReport();

  const handleExport = (col: PatientReportExportCol) => {
    exportColumn({ ...exportFilters, module, col });
  };

  const entries = data
    ? (Object.entries(data) as PatientReportEntry[])
    : (Object.entries(emptyData[module]) as PatientReportEntry[]);

  const getHeaderTooltip = (key: PatientReportKey): React.ReactNode => {
    if (key === 'twoWaySync') {
      return (
        <div className={s.tooltipContent}>
          <span>
            {t('controls.yes')}: {t('patientReportTooltips.twoWaySyncYes')}
          </span>
          <span>
            {t('controls.no')}: {t('patientReportTooltips.twoWaySyncNo')}
          </span>
        </div>
      );
    }

    const tooltipKey = getPatientReportTooltipKey(key, isRpm);

    return tooltipKey
      ? t(`patientReportTooltips.${tooltipKey}` as const)
      : null;
  };

  const tabs: IBaseTab<ModuleType>[] = [
    {
      key: ModuleType.RPM,
      label: t('tabs.rpm'),
    },
    {
      key: ModuleType.CCM,
      label: t('tabs.ccm'),
    },
  ];

  const handleChangeRoute = (tab: IBaseTab<ModuleType>) => {
    setModule(tab.key);
  };

  const renderLoadingIcon = () => (
    <ButtonLoadingIcon
      color={common.disabledIconColor}
      className={s.spinLoadingValue}
    />
  );

  return (
    <div className={s.reportCard}>
      <BaseSpinWrapper spinning={isLoading}>
        <div className={s['report-header']}>
          <div className={s.reportCardHeader}>
            <span className={s.reportCardIcon}>
              <PatientsReportIcon />
            </span>
            <span>{t('labels.patientsReport')}</span>
          </div>
          <BaseTabs
            tabs={tabs}
            type="rounded"
            size="small"
            activeKey={module}
            onChange={handleChangeRoute}
          />
        </div>
        <div className={s.reportCardBody}>
          <div className={s.detailSection}>
            <div className={s.codeTableRow}>
              <div className={s.codeTableGroup}>
                {entries.map(([key, value]) => {
                  const isTwoWaySync = key === 'twoWaySync';
                  const flexStyle = { flex: '1 1 auto' };
                  const tooltip = getHeaderTooltip(key);

                  return (
                    <div
                      key={key}
                      className={s.codeTableCell}
                      style={flexStyle}
                    >
                      <div className={s.codeTableHeader} style={flexStyle}>
                        <Tooltip title={tooltip} placement="top">
                          <div className={s.codeTableHeaderInfo}>
                            <span>
                              {t(`labels.${patientReportColumnLabelMap[key]}`)}{' '}
                            </span>
                            {tooltip && <InformationIcon />}
                          </div>
                        </Tooltip>
                      </div>
                      <div className={s.codeTableValueBlock}>
                        {isTwoWaySync && 'yes' in value ? (
                          <div
                            className={s['two-way-sync-col']}
                            style={flexStyle}
                          >
                            <div>
                              <span
                                className={`${s.codeTableValueClickable} ${
                                  exportingCol === 'twoWaySyncYes'
                                    ? s.codeTableValueExporting
                                    : ''
                                }`}
                                style={{ color: 'var(--green-50)' }}
                                onClick={() => handleExport('twoWaySyncYes')}
                              >
                                {exportingCol === 'twoWaySyncYes' ? (
                                  renderLoadingIcon()
                                ) : (
                                  <>
                                    <span>
                                      {t('controls.yes')}:{' '}
                                      {value.yes?.count ?? 0}{' '}
                                    </span>
                                    <span>({value.yes?.percent ?? 0}%)</span>
                                  </>
                                )}
                              </span>
                              <span style={{ paddingInline: 5 }} />
                              <span
                                className={`${s.codeTableValueClickable} ${
                                  exportingCol === 'twoWaySyncNo'
                                    ? s.codeTableValueExporting
                                    : ''
                                }`}
                                style={{ color: 'var(--red-50)' }}
                                onClick={() => handleExport('twoWaySyncNo')}
                              >
                                {exportingCol === 'twoWaySyncNo' ? (
                                  renderLoadingIcon()
                                ) : (
                                  <>
                                    <span>
                                      {t('controls.no')}: {value.no?.count ?? 0}{' '}
                                    </span>
                                    <span>({value.no?.percent ?? 0}%)</span>
                                  </>
                                )}
                              </span>
                            </div>
                            <div />
                          </div>
                        ) : (
                          <div style={flexStyle}>
                            {(() => {
                              const exportCol =
                                patientReportColumnExportMap[
                                  key as keyof typeof patientReportColumnExportMap
                                ];
                              const count =
                                'count' in value ? value.count ?? 0 : 0;
                              if (!exportCol) {
                                return count;
                              }
                              const isExportingCell =
                                exportingCol === exportCol;
                              return (
                                <span
                                  className={`${s.codeTableValueClickable} ${
                                    isExportingCell
                                      ? s.codeTableValueExporting
                                      : ''
                                  }`}
                                  onClick={() => handleExport(exportCol)}
                                >
                                  {isExportingCell
                                    ? renderLoadingIcon()
                                    : count}
                                </span>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </BaseSpinWrapper>
    </div>
  );
};
